import { defineStore, acceptHMRUpdate } from "pinia";
import { ref } from "vue";
import { useUserStore } from "./UserStore";
import client from "@/api/openapi-ts";
import { PaginatedData, PaginationArgs } from "@/types/api/common";
import {
  Event,
  Product,
  ProductArea,
  UserActivity,
} from "@/types/api/UserActivity";
import { KPIs } from "@/types/api/KPI";
import { User } from "@/types/api/User";

type CreateUserActivityParams = {
  event: string;
  meta_data: Record<string, any>;
  slack_thread_id?: string;
  pendo_properties?: Record<string, string>;
};

const defaultError: string = "An unexpected error occurred";

export const useUserActivityStore = defineStore("userActivity", () => {
  const loading = ref(false);
  const loadingAll = ref(false);
  const loadingKpis = ref(false);
  const loadingUserLoginKpis = ref(false);
  const loadingEvents = ref(false);
  const loadingProducts = ref(false);
  const loadingProductAreas = ref(false);
  const slackThreadId = ref(null);
  const userActivity = ref<PaginatedData<UserActivity>>({});
  const userActivityAll = ref<UserActivity[]>([]);
  const recentSearches = ref<UserActivity[]>([]);
  const events = ref<Event[]>([]);
  const products = ref<Product[]>([]);
  const productAreas = ref<ProductArea[]>([]);
  const selectedUserActivity = ref<UserActivity>();
  const kpis = ref<KPIs>();
  const userLoginKpis = ref<User[]>([]);
  const error = ref<string | undefined>(undefined);
  const userStore = useUserStore();
  const search = ref<{
    events: string;
    products: string;
    product_areas: string;
  }>({ events: "", products: "", product_areas: "" });

  const setRecentSearches = (payload: UserActivity[]) => {
    const org_id = userStore.currentOrg?.id;

    if (org_id) {
      recentSearches.value = payload.filter((p) => {
        return !p.meta_data?.org || p.meta_data?.org === org_id;
      });
    } else {
      recentSearches.value = payload;
    }
  };

  return {
    // -------------- Getters + Setters --------------
    loading,
    loadingAll,
    loadingKpis,
    loadingUserLoginKpis,
    loadingEvents,
    loadingProducts,
    loadingProductAreas,
    slackThreadId,
    userActivity,
    userActivityAll,
    recentSearches,
    selectedUserActivity,
    kpis,
    userLoginKpis,
    error,
    events,
    products,
    productAreas,
    search,
    // -------------- Complex Setters --------------
    setRecentSearches,

    // -------------- Computed --------------

    // -------------- Actions --------------
    create: async (args: CreateUserActivityParams) => {
      if (events.value.length === 0) {
        await useUserActivityStore().getEvents();
      }
      const event_id = events.value.find((e) => e.name === args.event)?.id;
      const payload: {
        event: string;
        event_id: number | undefined;
        meta_data: Record<string, any>;
        slack_thread_id?: string;
        pendo_data?: {
          type: "track";
          visitorId: string;
          accountId: string;
          timestamp: number;
          properties: Record<string, string>;
          context: Record<string, any>;
        };
      } = { event: args.event, event_id: event_id, meta_data: args.meta_data };
      if (slackThreadId.value) {
        payload.slack_thread_id = slackThreadId.value;
      }
      if (args.pendo_properties && window.pendo) {
        payload.pendo_data = {
          type: "track",
          visitorId: window.pendo.visitorId,
          accountId: String(window.pendo.accountId),
          timestamp: Date.now(),
          properties: args.pendo_properties,
          context: {
            userAgent: navigator.userAgent,
            url: window.location.href,
            title: document.title,
          },
        };
      }
      return await client.POST("/api/v1/user_activities", {
        body: payload as never,
      });
    },
    indexAll: async () => {
      const params: PaginationArgs = {
        ...userActivity.value!.args,
        per_page: 50,
        page: 1,
      };
      const totalItems = userActivity.value!.pagination?.total_items || 0;

      loadingAll.value = true;
      do {
        const { data } = await client.GET("/api/v1/user_activities", {
          params: { query: params as never },
        });
        userActivityAll.value.push(
          ...((data as unknown as UserActivity[]) ?? []),
        );
        params.page++;
      } while (userActivityAll.value.length < totalItems);
      loadingAll.value = false;
    },
    index: async (args: PaginationArgs) => {
      loading.value = true;
      try {
        const { data, error, response } = await client.GET(
          "/api/v1/user_activities",
          {
            params: { query: args as never },
          },
        );
        if (error) {
          throw error;
        }
        userActivity.value = {
          pagination: {
            page: Number(response.headers.get("current-page")),
            page_items: Number(response.headers.get("page-items")),
            total_pages: Number(response.headers.get("total-pages")),
            total_items: Number(response.headers.get("total-count")),
          },
          data: (data as unknown as UserActivity[]) ?? [],
          args: args,
        };
      } finally {
        loading.value = false;
      }
    },
    getRecentSearches: async () => {
      loading.value = true;
      try {
        const { data, error } = await client.GET(
          "/api/v1/user_activities/recent_searches",
        );
        if (error) {
          throw error;
        }
        setRecentSearches((data as unknown as UserActivity[]) ?? []);
      } finally {
        loading.value = false;
      }
    },
    getKpis: async (args: { start_date: string; end_date: string }) => {
      loadingKpis.value = true;
      try {
        const { data } = await client.GET("/api/v1/user_activities/kpis", {
          params: { query: args as never },
        });
        kpis.value = (data as unknown as KPIs) ?? undefined;
      } finally {
        loadingKpis.value = false;
      }
    },
    getUserLoginKpis: async (args: {
      start_date: string;
      end_date: string;
      min_logins: number;
      max_logins: number;
    }) => {
      loadingUserLoginKpis.value = true;
      userLoginKpis.value = [];
      try {
        const { data } = await client.GET(
          "/api/v1/user_activities/user_logins",
          {
            params: { query: args as never },
          },
        );
        userLoginKpis.value = (data as unknown as User[]) ?? [];
      } finally {
        loadingUserLoginKpis.value = false;
      }
    },
    createEvent: async (args: { name: string; display: string }) => {
      loadingEvents.value = true;
      try {
        const { data, error: requestError } = await client.POST(
          "/api/v1/user_activities/events",
          { body: args as never },
        );
        if (requestError) throw requestError;
        events.value = [...events.value, data as unknown as Event];
      } catch (e: any) {
        error.value = e.response?.data?.message || e.message || defaultError;
      } finally {
        loadingEvents.value = false;
      }
    },

    createProduct: async (args: { name: string; display: string }) => {
      loadingProducts.value = true;
      try {
        const { data, error: requestError } = await client.POST(
          "/api/v1/user_activities/products",
          { body: args as never },
        );
        if (requestError) throw requestError;
        products.value = [...products.value, data as unknown as Product];
      } catch (e: any) {
        error.value = e.response?.data?.message || e.message || defaultError;
      } finally {
        loadingProducts.value = false;
      }
    },

    createProductArea: async (args: { name: string; display: string }) => {
      loadingProductAreas.value = true;
      try {
        const { data, error: requestError } = await client.POST(
          "/api/v1/user_activities/product_areas",
          { body: args as never },
        );
        if (requestError) throw requestError;
        productAreas.value = [
          ...productAreas.value,
          data as unknown as ProductArea,
        ];
      } catch (e: any) {
        error.value = e.response?.data?.message || e.message || defaultError;
      } finally {
        loadingProductAreas.value = false;
      }
    },
    getEvents: async () => {
      loadingEvents.value = true;
      try {
        const { data, error: requestError } = await client.GET(
          "/api/v1/user_activities/events",
        );
        if (requestError) throw requestError;
        events.value = (data as unknown as Event[]) ?? [];
      } catch (e: any) {
        error.value = e.response?.data?.message || e.message || defaultError;
      } finally {
        loadingEvents.value = false;
      }
    },

    getProducts: async () => {
      loadingProducts.value = true;
      try {
        const { data, error: requestError } = await client.GET(
          "/api/v1/user_activities/products",
        );
        if (requestError) throw requestError;
        products.value = (data as unknown as Product[]) ?? [];
      } catch (e: any) {
        error.value = e.response?.data?.message || e.message || defaultError;
      } finally {
        loadingProducts.value = false;
      }
    },

    getProductAreas: async () => {
      loadingProductAreas.value = true;
      try {
        const { data, error: requestError } = await client.GET(
          "/api/v1/user_activities/product_areas",
        );
        if (requestError) throw requestError;
        productAreas.value = (data as unknown as ProductArea[]) ?? [];
      } catch (e: any) {
        error.value = e.response?.data?.message || e.message || defaultError;
      } finally {
        loadingProductAreas.value = false;
      }
    },
    updateEvent: async (
      id: number,
      args: { name: string; display: string },
    ) => {
      loadingEvents.value = true;
      try {
        const { data, error: requestError } = await client.PUT(
          "/api/v1/user_activities/events/{event_id}",
          {
            params: { path: { event_id: id } },
            body: args as never,
          },
        );
        if (requestError) throw requestError;
        events.value = events.value.map((item) =>
          item.id === id ? (data as unknown as Event) : item,
        );
      } catch (e: any) {
        error.value = e.response?.data?.message || e.message || defaultError;
      } finally {
        loadingEvents.value = false;
      }
    },

    updateProduct: async (
      id: number,
      args: { name: string; display: string },
    ) => {
      loadingProducts.value = true;
      try {
        const { data, error: requestError } = await client.PUT(
          "/api/v1/user_activities/products/{product_id}",
          {
            params: { path: { product_id: id } },
            body: args as never,
          },
        );
        if (requestError) throw requestError;
        products.value = products.value.map((item) =>
          item.id === id ? (data as unknown as Product) : item,
        );
      } catch (e: any) {
        error.value = e.response?.data?.message || e.message || defaultError;
      } finally {
        loadingProducts.value = false;
      }
    },

    updateProductArea: async (
      id: number,
      args: { name: string; display: string },
    ) => {
      loadingProductAreas.value = true;
      try {
        const { data, error: requestError } = await client.PUT(
          "/api/v1/user_activities/product_areas/{product_area_id}",
          {
            params: { path: { product_area_id: id } },
            body: args as never,
          },
        );
        if (requestError) throw requestError;
        productAreas.value = productAreas.value.map((item) =>
          item.id === id ? (data as unknown as ProductArea) : item,
        );
      } catch (e: any) {
        error.value = e.response?.data?.message || e.message || defaultError;
      } finally {
        loadingProductAreas.value = false;
      }
    },
    deleteEvent: async (id: number) => {
      loadingEvents.value = true;
      try {
        const { error: requestError } = await client.DELETE(
          "/api/v1/user_activities/events/{event_id}",
          {
            params: { path: { event_id: id } },
          },
        );
        if (requestError) throw requestError;
        events.value = events.value.filter((item) => item.id !== id);
      } catch (e: any) {
        error.value = e.response?.data?.message || e.message || defaultError;
      } finally {
        loadingEvents.value = false;
      }
    },

    deleteProduct: async (id: number) => {
      loadingProducts.value = true;
      try {
        const { error: requestError } = await client.DELETE(
          "/api/v1/user_activities/products/{product_id}",
          {
            params: { path: { product_id: id } },
          },
        );
        if (requestError) throw requestError;
        products.value = products.value.filter((item) => item.id !== id);
      } catch (e: any) {
        error.value = e.response?.data?.message || e.message || defaultError;
      } finally {
        loadingProducts.value = false;
      }
    },

    deleteProductArea: async (id: number) => {
      loadingProductAreas.value = true;
      try {
        const { error: requestError } = await client.DELETE(
          "/api/v1/user_activities/product_areas/{product_area_id}",
          {
            params: { path: { product_area_id: id } },
          },
        );
        if (requestError) throw requestError;
        productAreas.value = productAreas.value.filter(
          (item) => item.id !== id,
        );
      } catch (e: any) {
        error.value = e.response?.data?.message || e.message || defaultError;
      } finally {
        loadingProductAreas.value = false;
      }
    },
  };
});

if (import.meta.hot) {
  import.meta.hot.accept(
    acceptHMRUpdate(useUserActivityStore, import.meta.hot),
  );
}
